import { useState, useEffect } from "react";
import {
    apiService,
    type AnalyticsStudentDetail as StudentDetailData,
    type AnalyticsSessionDetail,
    type AnalyticsBiteDetail,
    type SessionTimelineResponse,
    type SlideTimelineEntry,
} from "../../services/api.service";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

interface Props {
    userId: number;
    sessionId: string | null;
    onBack: () => void;
    onSessionClick: (sessionId: string) => void;
}

function msToMinutes(ms: number): string {
    const minutes = Math.round(ms / 60000);
    return `${minutes}m`;
}

function formatTs(ts: number | null): string {
    if (!ts) return "-";
    return new Date(ts).toLocaleString();
}

function biteSegmentColor(bite: AnalyticsBiteDetail): string {
    if (bite.bite_status === "incomplete" || bite.bite_status === "not_started") return "#ef4444";
    if (bite.tutor_support_level === "high") return "#ef4444";
    if (bite.tutor_support_level === "medium") return "#f59e0b";
    if (bite.tutor_support_level === "low" || bite.tutor_support_level === "none") return "#fbbf24";
    if (bite.bite_status === "flawless") return "#10b981";
    return "#10b981";
}

function biteSegmentLabel(bite: AnalyticsBiteDetail): string {
    const status = bite.bite_status || "unknown";
    const support = bite.tutor_support_level ? ` (${bite.tutor_support_level})` : "";
    return `Slide ${bite.slide_idx}: ${status}${support} - ${msToMinutes(bite.time_spent_ms)}`;
}

function completionColor(pct: number): string {
    if (pct >= 75) return "text-emerald-600";
    if (pct >= 40) return "text-amber-500";
    return "text-red-500";
}

function SlideTimeline({ timeline }: { timeline: SessionTimelineResponse }) {
    const totalMs = timeline.total_duration_ms || 1;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Navigation Timeline</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                    Slides in chronological order. Red = backward revisit. Width = time spent.
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Timeline swimlane */}
                <div className="flex rounded-lg overflow-hidden h-10 bg-muted gap-px">
                    {timeline.slides.map((s, i) => {
                        const widthPct = Math.max((s.time_on_slide_ms / totalMs) * 100, 1);
                        const bg = s.is_backward ? "#ef4444" : "#10b981";
                        const label = `Slide ${s.slide_index}: ${msToMinutes(s.time_on_slide_ms)}${s.is_backward ? " (revisit)" : ""}\n${s.message_count} messages`;
                        return (
                            <div
                                key={i}
                                className="flex items-center justify-center min-w-[16px] transition-opacity hover:opacity-75 cursor-default"
                                style={{ width: `${widthPct}%`, backgroundColor: bg }}
                                title={label}
                            >
                                <span className="text-[9px] font-bold text-white drop-shadow-sm select-none">
                                    {s.slide_index}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="flex gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" /> Forward visit
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" /> Backward revisit
                    </span>
                </div>

                {/* Per-slide rows */}
                <div className="space-y-1 max-h-64 overflow-y-auto">
                    {timeline.slides.map((s: SlideTimelineEntry, i: number) => (
                        <div
                            key={i}
                            className={cn(
                                "grid grid-cols-[56px_1fr_80px_80px_80px] gap-2 px-3 py-2 rounded text-sm items-center",
                                s.is_backward ? "bg-red-50 border border-red-200" : "bg-muted/40",
                            )}
                        >
                            <div className="font-semibold text-xs">
                                {s.is_backward ? (
                                    <span className="text-red-600">↩ {s.slide_index}</span>
                                ) : (
                                    <span>→ {s.slide_index}</span>
                                )}
                            </div>
                            <div className="h-2 rounded bg-gray-200 overflow-hidden">
                                <div
                                    className="h-full rounded"
                                    style={{
                                        width: `${Math.min(100, (s.time_on_slide_ms / totalMs) * 100 * 3)}%`,
                                        backgroundColor: s.is_backward ? "#ef4444" : "#10b981",
                                    }}
                                />
                            </div>
                            <div className="text-right text-xs tabular-nums text-muted-foreground">
                                {msToMinutes(s.time_on_slide_ms)}
                            </div>
                            <div className="text-right text-xs tabular-nums text-muted-foreground">
                                {s.message_count} msgs
                            </div>
                            <div className="text-right text-xs text-muted-foreground">
                                {new Date(s.slide_start_ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

export const AnalyticsStudentDetail = ({ userId, sessionId, onBack, onSessionClick }: Props) => {
    const [studentData, setStudentData] = useState<StudentDetailData | null>(null);
    const [sessionDetail, setSessionDetail] = useState<AnalyticsSessionDetail | null>(null);
    const [sessionTimeline, setSessionTimeline] = useState<SessionTimelineResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                setError(null);
                if (sessionId) {
                    const [detail, timeline] = await Promise.allSettled([
                        apiService.getAnalyticsSessionDetail(userId, sessionId),
                        apiService.getSessionTimeline(sessionId),
                    ]);
                    if (detail.status === "fulfilled") setSessionDetail(detail.value);
                    if (timeline.status === "fulfilled") setSessionTimeline(timeline.value);
                    if (detail.status === "rejected") throw detail.reason;
                } else {
                    const data = await apiService.getAnalyticsStudentDetail(userId);
                    setStudentData(data);
                    setSessionDetail(null);
                    setSessionTimeline(null);
                }
            } catch {
                setError("Failed to load data");
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [userId, sessionId]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>Loading...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>{error}</p>
                <Button variant="outline" className="mt-4" onClick={onBack}>Go Back</Button>
            </div>
        );
    }

    // Session bite breakdown view
    if (sessionId && sessionDetail) {
        const totalTime = sessionDetail.bites.reduce((sum, b) => sum + b.time_spent_ms, 0) || 1;

        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={onBack}>Back</Button>
                    <h2 className="text-lg font-semibold text-foreground">
                        Session: {sessionDetail.lesson_name || `Lesson ${sessionDetail.lesson_id}`}
                    </h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-6">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Completion</p>
                            <p className={cn("text-3xl font-bold tabular-nums", completionColor(sessionDetail.completion_pct))}>
                                {sessionDetail.completion_pct}<span className="text-lg text-muted-foreground ml-0.5">%</span>
                            </p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Time Spent</p>
                            <p className="text-3xl font-bold tabular-nums">{msToMinutes(sessionDetail.time_spent_ms)}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Status</p>
                            <p className="text-xl font-bold">{sessionDetail.lesson_status}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Bites</p>
                            <p className="text-3xl font-bold tabular-nums">{sessionDetail.bites.length}</p>
                        </CardContent>
                    </Card>
                </div>

                {sessionDetail.bites.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Bite Progress</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex rounded-lg overflow-hidden h-8 bg-muted gap-px">
                                {sessionDetail.bites.map((b) => {
                                    const widthPct = Math.max((b.time_spent_ms / totalTime) * 100, 2);
                                    return (
                                        <div
                                            key={b.slide_idx}
                                            className="flex items-center justify-center min-w-[20px] transition-opacity hover:opacity-80 cursor-default"
                                            style={{
                                                width: `${widthPct}%`,
                                                backgroundColor: biteSegmentColor(b),
                                            }}
                                            title={biteSegmentLabel(b)}
                                        >
                                            <span className="text-[10px] font-bold text-white drop-shadow-sm">{b.slide_idx}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1.5">
                                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#10b981" }} /> Completed
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#fbbf24" }} /> Low/No support
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#f59e0b" }} /> Medium support
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#ef4444" }} /> High / Incomplete
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Bite Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {sessionDetail.bites.length === 0 ? (
                            <p className="text-center py-12 text-muted-foreground">No bite data</p>
                        ) : (
                            <div className="space-y-2">
                                {sessionDetail.bites.map((b: AnalyticsBiteDetail) => (
                                    <div key={b.slide_idx} className="grid grid-cols-[60px_1fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2.5 bg-muted/50 rounded-md text-sm items-center">
                                        <div className="font-semibold text-foreground">Slide {b.slide_idx}</div>
                                        <div>
                                            <span className="text-[11px] text-muted-foreground block">Status</span>
                                            {b.bite_status}
                                        </div>
                                        <div>
                                            <span className="text-[11px] text-muted-foreground block">Support</span>
                                            {b.tutor_support_level || "-"}
                                        </div>
                                        <div>
                                            <span className="text-[11px] text-muted-foreground block">Time</span>
                                            {msToMinutes(b.time_spent_ms)}
                                        </div>
                                        <div>
                                            <span className="text-[11px] text-muted-foreground block">Correct</span>
                                            {b.attempts_correct}/{b.attempts_total}
                                        </div>
                                        <div>
                                            <span className="text-[11px] text-muted-foreground block">Asks</span>
                                            S:{b.student_asked_count} AI:{b.ai_asked_count}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {sessionTimeline && sessionTimeline.slides.length > 0 && (
                    <SlideTimeline timeline={sessionTimeline} />
                )}
            </div>
        );
    }

    // Student sessions list view
    if (!studentData) return null;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={onBack}>Back to Students</Button>
                <h2 className="text-lg font-semibold text-foreground">{studentData.name || `User ${studentData.user_id}`}</h2>
                <span className="text-sm text-muted-foreground">
                    {studentData.total_sessions} sessions
                </span>
            </div>

            {studentData.sessions.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">No sessions found</p>
            ) : (
                <div className="space-y-2">
                    {studentData.sessions.map((s) => (
                        <Card
                            key={s.session_id}
                            className="cursor-pointer hover:border-primary transition-colors"
                            onClick={() => onSessionClick(s.session_id)}
                        >
                            <CardContent className="p-4">
                                <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2 items-center">
                                    <div className="text-sm">
                                        <span className="text-[11px] text-muted-foreground block">Lesson</span>
                                        <span className="font-medium">{s.lesson_name || `ID: ${s.lesson_id}`}</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-[11px] text-muted-foreground block">Started</span>
                                        {formatTs(s.session_start_ts)}
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-[11px] text-muted-foreground block">Completion</span>
                                        <span className={cn("font-semibold tabular-nums", completionColor(s.completion_pct))}>
                                            {s.completion_pct}%
                                        </span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-[11px] text-muted-foreground block">Time</span>
                                        <span className="tabular-nums">{msToMinutes(s.time_spent_ms)}</span>
                                    </div>
                                    {s.max_slide_reached != null && s.total_slides != null && (
                                        <div className="text-sm">
                                            <span className="text-[11px] text-muted-foreground block">Progress</span>
                                            <span className="tabular-nums">{s.max_slide_reached}/{s.total_slides} slides</span>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};
